import { supabaseClient } from "../supabase";
import { AddressSuggestionEF, FeatureMember, GeocoderResponseMetaDataType } from "./types";
import {
  convertAddressInputCreateForSupa,
  convertAddressSuggestion,
  convertAddressUpdateForSupa,
  convertSupaAddress,
} from "./converters";
import { BaseAddressClient, AddressSuggestion, Address, AddressInput, User } from "@core/types";
import { Tables } from "../types/supabase";

type AddressSuggestionEFType = {
  addresses: AddressSuggestionEF[];
  raw: {
    featureMember: FeatureMember[];
    GeocoderResponseMetaData: GeocoderResponseMetaDataType;
  };
};

class AddressClient extends BaseAddressClient {
  async getAddressSuggestion(address: string): Promise<AddressSuggestion[] | null> {
    if (!address) return null;

    const { data, error } = await supabaseClient.functions.invoke<AddressSuggestionEFType>("address-suggestion", {
      body: {
        query: address,
      },
    });

    if (error) {
      throw error;
    }

    return !!data?.addresses?.length ? data.addresses.map(convertAddressSuggestion) : null;
  }

  async getCustomerAddresses(userId: User["id"]): Promise<Address[] | null> {
    if (!userId) return null;

    const { data } = await supabaseClient
      .from("customer_addresses")
      .select("*")
      .eq("customer_id", parseInt(userId))
      .throwOnError();

    const addresses = data?.map(convertSupaAddress);

    return !!addresses?.length ? addresses : null;
  }

  async createCustomerAddress(customerAddressInput: AddressInput, userId: User["id"]): Promise<Address | null> {
    if (!userId) return null;

    const { location: _, ...customerSupa } = convertAddressInputCreateForSupa(customerAddressInput, userId);
    const { data: newAddress, error } = await supabaseClient.functions.invoke<Tables<"customer_addresses">>(
      "customer-addresses",
      {
        method: "POST",
        body: {
          ...customerSupa,
          latitude: customerAddressInput.latitude ?? "",
          longitude: customerAddressInput.longitude ?? "",
        },
      },
    );

    if (error) {
      console.log("error", error);
    }

    return !!newAddress ? convertSupaAddress(newAddress) : null;
  }

  async deleteCustomerAddress(addressId: Address["id"]): Promise<Address["id"] | null> {
    const { data } = await supabaseClient
      .from("customer_addresses")
      .delete()
      .eq("id", parseInt(addressId!))
      .select()
      .single()
      .throwOnError();

    const deletedAddressId = data?.["id"];

    return !!deletedAddressId ? String(deletedAddressId) : null;
  }

  async updateCustomerAddress(customerAddress: Address): Promise<Address | null> {

    const { location: _, ...customerSupa } = convertAddressUpdateForSupa(customerAddress);
    const { data: updatedAddress, error } = await supabaseClient.functions.invoke<Tables<"customer_addresses">>(
      "customer-addresses",
      {
        method: "PUT",
        body: {
          ...customerSupa,
          id: parseInt(customerAddress.id!),
          latitude: customerAddress.latitude ?? "",
          longitude: customerAddress.longitude ?? "",
        },
      },
    );

    if (error) {
      throw error;
    }

    return !!updatedAddress ? convertSupaAddress(updatedAddress) : null;
  }
}

export default AddressClient;
